// @flow

import * as React from 'react';
import qs from 'qs';
import {useAccount} from 'docs/data';
import {Spinner} from '../cli/general';
import CollapsibleJson from '../../components/CollapsibleJson';

export type Param = {
  [string]: {
    type: 'param',
    description?: string,
    shorthand?: string,
    repeatable?: boolean,
    no_value?: boolean,
    setAllowInput?: (boolean) => void,
  },
};

export type Command = {
  type: 'command',
  description?: string,
  params?: Param,
  path?: string,
  method?: string,
  run?: boolean,
  args?: Array<string>,
  // eslint-disable-next-line no-use-before-define
  commands?: Commands,
};

export type Commands = {
  [string]: Command,
};

const normalizeParams = (params: {[string]: mixed}): {[string]: mixed} =>
  Object.fromEntries(
    Object.entries(params).map(([k, v]) => [k.replace(/-/g, '_'), v]),
  );

export const makeStripeRequest = async ({
  url,
  method,
  raw_params,
  api_key,
  additional_headers,
}: {
  url: string,
  method: string,
  raw_params: {[string]: any},
  api_key: string,
  additional_headers: {[string]: string},
}): Promise<any> => {
  // Create headers
  const headers = {};
  const params = normalizeParams(Object.assign({}, raw_params));
  headers['Content-Type'] = 'application/x-www-form-urlencoded';
  headers.Authorization = `Bearer ${api_key}`;
  if (raw_params && raw_params['stripe-account']) {
    headers['Stripe-Account'] = raw_params['stripe-account'];
    delete params['stripe-account'];
  }
  if (raw_params && 'stripe-version' in raw_params) {
    headers['Stripe-Version'] = raw_params['stripe-version'];
    delete params['stripe-version'];
  }

  for (const [key, value] of Object.entries(additional_headers)) {
    headers[key] = value;
  }

  const encodedParams = qs.stringify(params);

  return fetch(method === 'get' ? `${url}?${encodedParams}` : url, {
    method,
    headers,
    ...(method !== 'get' ? {body: encodedParams} : {}),
  }).then((res) => res.json());
};

// Make the request and get the response.
export const JsonResponse = ({
  path,
  method,
  params,
  api_key,
  setAllowInput,
}: {
  path: string,
  method: string,
  params: {[string]: string},
  api_key: string,
  setAllowInput: (boolean) => void,
}) => {
  const [output, setOutput] = React.useState(null);
  const [error, setError] = React.useState(null);
  const {account} = useAccount();

  React.useEffect(() => {
    makeStripeRequest({
      url: `${account.hosts['api.stripe.com']}${path}`,
      method,
      api_key,
      raw_params: params ? {...params} : {},
      additional_headers: {},
    })
      .then((s) => {
        setOutput(s);
        setAllowInput(true);
      })
      .catch((err) => {
        setError(err.message);
      });
  }, [account, api_key, method, params, path, setAllowInput]);

  if (error) {
    return <pre>{error}</pre>;
  } else if (output) {
    return <CollapsibleJson data={output} />;
  } else {
    return <Spinner text="Fetching from the Stripe API..." />;
  }
};
